import {
  TravellerDocumentDownloadSchema,
  TravellerDocumentRequestListSchema,
  TravellerDocumentRequestSchema,
} from '@utazzo/shared';

import type {
  CreateTravellerDocumentRequestInput,
  TravellerDocumentDownload,
  TravellerDocumentRequest,
  TravellerDocumentTripScope,
  TravellerDocumentUploadInput,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/** Traveller document (PAN / passport) endpoints (apps/api modules/traveller-documents). */

export const fetchLeadDocumentRequests = async (
  leadId: string,
): Promise<TravellerDocumentRequest[]> => {
  const { data } = await apiClient.get<unknown>('/traveller-documents', { params: { leadId } });
  return TravellerDocumentRequestListSchema.parse(data);
};

export const createDocumentRequest = async (
  input: CreateTravellerDocumentRequestInput,
): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>('/traveller-documents', input);
  return TravellerDocumentRequestSchema.parse(data);
};

export const updateDocumentRequest = async (
  id: string,
  input: {
    staffNote?: string | null;
    travellerCount?: number;
    tripScope?: TravellerDocumentTripScope;
  },
): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.patch<unknown>(`/traveller-documents/${id}`, input);
  return TravellerDocumentRequestSchema.parse(data);
};

export const resendDocumentRequest = async (id: string): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/${id}/resend`);
  return TravellerDocumentRequestSchema.parse(data);
};

/** Staff upload on the customer's behalf (ops case screen). */
export const uploadDocumentFile = async (
  id: string,
  input: TravellerDocumentUploadInput,
): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/${id}/files`, input, {
    timeout: 120_000,
  });
  return TravellerDocumentRequestSchema.parse(data);
};

export const verifyDocumentRequest = async (id: string): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/${id}/verify`);
  return TravellerDocumentRequestSchema.parse(data);
};

export const reopenDocumentRequest = async (id: string): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/${id}/reopen`);
  return TravellerDocumentRequestSchema.parse(data);
};

export const cancelDocumentRequest = async (id: string): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/${id}/cancel`);
  return TravellerDocumentRequestSchema.parse(data);
};

export const fetchDocumentFileDownload = async (
  fileId: string,
  opts: { attachment?: boolean } = {},
): Promise<TravellerDocumentDownload> => {
  const { data } = await apiClient.get<unknown>(`/traveller-documents/files/${fileId}/download`, {
    params: opts.attachment ? { disposition: 'attachment' } : undefined,
  });
  return TravellerDocumentDownloadSchema.parse(data);
};

export const rejectDocumentFile = async (
  fileId: string,
  reason: string,
): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.post<unknown>(`/traveller-documents/files/${fileId}/reject`, {
    reason,
  });
  return TravellerDocumentRequestSchema.parse(data);
};

export const removeDocumentFile = async (fileId: string): Promise<TravellerDocumentRequest> => {
  const { data } = await apiClient.delete<unknown>(`/traveller-documents/files/${fileId}`);
  return TravellerDocumentRequestSchema.parse(data);
};
